import { z } from 'zod';

export const titanFeatureFlagNameSchema = z.enum(['featureOneTrust', 'featureDeepLinkingForceInit']);
export type TitanFeatureFlagName = z.infer<typeof titanFeatureFlagNameSchema>;

type TitanFeatureFlagRollout = {
  /** The percentage of users that will experience the variant and be tracked as in the variant. */
  variantPercent: number;
  /** The percentage of users that will be tracked as in the control. */
  controlPercent: number;
};

type TitanFeatureFlagEnabledCondition = {
  /**
   * These are 2 digit region codes. Such as CA for California. These are set by the `cf-region-code` header.\
   * See [Cloudflare docs](https://developers.cloudflare.com/rules/transform/managed-transforms/reference/#add-visitor-location-headers) for more info.
   */
  regionCodes?: string[];
  /**
   * These are 2 digit country codes. Such as US for United States. These are set by the `cf-ipcountry` header.\
   * See [Cloudflare docs](https://developers.cloudflare.com/rules/transform/managed-transforms/reference/#add-visitor-location-headers) for more info.
   *
   * Enabled `allEuCountries` takes precedence over this field.
   */
  countryCodes?: string[];
  /**
   * If enabled, the flag will be active for all EU countries.
   *
   * When enabled, this field takes precedence over `countryCodes`.
   */
  allEuCountries?: boolean;
};

/**
 * Apps whose Titan flag names live in `feature_flag` (or share Galatea's rows — see
 * `titanFeatureFlagsConfig.audiobooks`). Distinct from the DB `app_name` enum, which has no
 * `audiobooks` value: Audiobooks Plus reuses Galatea's flag-name rows and applies its own
 * targeting/rollout here.
 */
export type TitanFeatureFlagAppName = 'galatea' | 'candyjar' | 'audiobooks';

export type TitanFeatureFlagConfig = Record<
  TitanFeatureFlagAppName,
  Record<
    TitanFeatureFlagName,
    {
      /**
       * Conditions that must be met for the feature flag to be considered for enabling.
       */
      strategies: TitanFeatureFlagEnabledCondition[];
      /**
       * Determined by device ID hashing. This happens after targeting checks.
       *
       * The amount of users that will have this feature flag enabled. These values are out of 100, and any remaining will not see the experience. For example,
       * if you set `variantPercent` to 20 and `controlPercent` to 30, then 20% of users will see the variant experience, 30% will be in the control group, and
       * 50% will not see the experience at all.
       *
       * The control group is effectively only used for tracking, and will receive a disabled feature flag response.
       */
      rollout: TitanFeatureFlagRollout;
    }
  >
>;

export type FeatureFlagResolutionProps = {
  deviceId: string;
  regionCode?: string;
  countryCode?: string;
};
